import { state } from "./state.js";
import { SEED, isFreebie } from "./catalogue.js";
import { hasDB, sbGet } from "./supabase.js";
import { track } from "./presence.js";
import { syncCart, toast, openCart, renderModules, buyableFor } from "./render.js";

const PRODUCT_COLUMNS = "id,code,title,description,module_slug,price_cents,sort_order,preview_pages,page_count,created_at";

const CART_KEY = "cta_cart";

/**
 * Restore the cart saved by a previous visit, if there is one. Called once
 * at startup, before the first render, so a refresh doesn't lose it —
 * that's the whole point. Each cart line is a full snapshot (id, code,
 * title, module_slug, price_cents) rather than just an id, so this works
 * even before loadProducts() below has resolved.
 */
export function loadCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (raw) state.cart = JSON.parse(raw);
  } catch {
    // localStorage unavailable (e.g. private browsing) — cart just starts empty, as it always did before this.
  }
}

export function saveCart() {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(state.cart));
  } catch {
    // ignore — worst case the cart doesn't survive a reload, same as before this existed
  }
}

/**
 * Load the catalogue.
 * With Supabase configured: only rows where is_active = true, ordered by sort_order.
 * Without it (or if the request fails): the local SEED list, so the site still works.
 */
export async function loadProducts() {
  let live = true;
  if (hasDB) {
    try {
      state.products = await sbGet(
        `products?select=${PRODUCT_COLUMNS}&is_active=eq.true&order=sort_order.asc`
      );
    } catch (err) {
      console.warn("Supabase unavailable, using local catalogue:", err.message);
      state.products = SEED;
      live = false; // a brief outage shouldn't wipe a real cart — leave it as saved
    }
  } else {
    state.products = SEED;
  }
  if (live) refreshCart();
  renderModules(); // now that state.products is populated, the per-module section counts are real
  syncCart();
}

/**
 * Bring a restored cart in line with the catalogue that just loaded. Cart
 * lines are snapshots from whenever they were added, so a note may since
 * have been removed or repriced — and the database charges the live price
 * (price_order()), so the cart has to show that one too. Also drops SEED
 * rows ("seed-…" ids) added while Supabase was unreachable, and any note
 * since moved to Freebies (price_order() would refuse it).
 */
function refreshCart() {
  const byId = new Map(state.products.filter((p) => !isFreebie(p)).map((p) => [p.id, p]));
  const before = state.cart.length;
  state.cart = state.cart
    .filter((c) => byId.has(c.id))
    .map((c) => {
      const p = byId.get(c.id);
      return { id: p.id, code: p.code, title: p.title, module_slug: p.module_slug, price_cents: p.price_cents };
    });
  const removed = before - state.cart.length;
  if (!state.cart.length) state.checkoutStep = "cart";
  saveCart();
  if (removed) {
    toast(`${removed} note${removed === 1 ? " in your cart is" : "s in your cart are"} no longer available, so ${removed === 1 ? "it's" : "they've"} been removed`);
  }
}

/** Add a section. One of each — buying the same PDF twice makes no sense. */
export function addToCart(id) {
  const product = state.products.find((p) => p.id === id);
  if (!product || isFreebie(product)) return;

  if (state.cart.some((c) => c.id === id)) {
    toast("Already in your cart");
    openCart();
    return;
  }

  state.cart.push({
    id: product.id,
    code: product.code,
    title: product.title,
    module_slug: product.module_slug,
    price_cents: product.price_cents,
  });
  state.checkoutStep = "cart";
  saveCart();
  syncCart();
  track("cart");
  toast(`${product.title} added to cart`);
}

/** The "Select" menu — every note in a module (or "all") that isn't already in the cart. */
export function addAllToCart(slug) {
  const missing = buyableFor(slug).filter((p) => !state.cart.some((c) => c.id === p.id));
  if (!missing.length) return;
  missing.forEach((p) =>
    state.cart.push({ id: p.id, code: p.code, title: p.title, module_slug: p.module_slug, price_cents: p.price_cents })
  );
  state.checkoutStep = "cart";
  saveCart();
  syncCart();
  track("cart");
  toast(`${missing.length} note${missing.length === 1 ? "" : "s"} added to cart`);
  openCart();
}

export function removeFromCart(id) {
  state.cart = state.cart.filter((c) => c.id !== id);
  if (!state.cart.length) state.checkoutStep = "cart";
  saveCart();
  syncCart();
}
